import React, {useState} from "react";
import {Lock, Eye, EyeOff, CheckCircle, XCircle} from "lucide-react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {API_URL} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import Notification from "../../components/Common/Notification";
import FormHeader from "../../components/Common/FormHeader";
import {FormSection, inputClass} from "../../components/Setting/SettingsUI";

// Declared outside the page: a component created during render remounts its
// input on every keystroke and loses the focus.
const PasswordField = ({id, name, label, value, onChange, show, onToggle, placeholder, autoComplete}) => (
    <div>
        <label htmlFor={id} className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            {label}<span className="text-red-500"> *</span> :
        </label>
        <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <input
                id={id}
                name={name}
                type={show ? "text" : "password"}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                className={`${inputClass(false)} pl-9 pr-10`}
                placeholder={placeholder}
                required
            />
            <button
                type="button"
                onClick={onToggle}
                aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
            >
                {show ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
        </div>
    </div>
);

const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 1) return {label: 'Faible', level: 1, textColor: 'text-red-500', barColor: 'bg-red-400'};
    if (score === 2) return {label: 'Moyen', level: 2, textColor: 'text-amber-500', barColor: 'bg-amber-400'};
    if (score === 3) return {label: 'Bon', level: 3, textColor: 'text-primary', barColor: 'bg-primary'};
    return {label: 'Fort', level: 4, textColor: 'text-emerald-500', barColor: 'bg-emerald-500'};
};

const ChangePasswordLoggedIn = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState({show: false, type: '', message: ''});

    const hideNotification = () => setNotification({show: false, type: '', message: ''});

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type !== 'loading') {
            setTimeout(hideNotification, 3000);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({...prev, [field]: !prev[field]}));
    };

    const strength = getPasswordStrength(formData.newPassword);
    const passwordsMatch = formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword;

    const validateForm = () => {
        if (!formData.currentPassword) {
            showNotification('error', 'Veuillez saisir votre mot de passe actuel');
            return false;
        }

        if (formData.newPassword.length < 8) {
            showNotification('error', 'Le nouveau mot de passe doit contenir au moins 8 caractères');
            return false;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            showNotification('error', 'Les mots de passe ne correspondent pas');
            return false;
        }

        if (formData.currentPassword === formData.newPassword) {
            showNotification('error', 'Le nouveau mot de passe doit être différent de l\'ancien');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        e?.stopPropagation();

        // Prevent double submission
        if (isLoading) {
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setIsLoading(true);
            showNotification('loading', 'Modification du mot de passe en cours...');

            const accountInfo = await secureStorage.getItem("selectedAccount");
            const token = await secureStorage.getItem("token");

            // Use direct axios call without the 401 interceptor that auto-redirects
            const response = await axios.put(
                `${API_URL}/auth/change-password`,
                {
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-account-id': accountInfo?.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Only proceed if request was successful
            if (response.status === 200 || response.status === 201) {
                showNotification('success', 'Mot de passe modifié avec succès');

                setFormData({currentPassword: "", newPassword: "", confirmPassword: ""});

                // Navigate back to settings after 2 seconds
                setTimeout(() => {
                    navigate('/settings');
                }, 2000);
            }

        } catch (error) {
            console.error("Error changing password:", error);

            // Handle different error cases without auto-logout
            let errorMessage = "Une erreur est survenue lors de la modification du mot de passe";

            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = "Mot de passe actuel incorrect";
                } else if (error.response.status === 400) {
                    errorMessage = error.response.data?.detail ||
                                  error.response.data?.message ||
                                  "Données invalides";
                } else if (error.response.status === 404) {
                    errorMessage = "Service non disponible";
                } else {
                    errorMessage = error.response.data?.detail ||
                                  error.response.data?.message ||
                                  errorMessage;
                }
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur";
            }

            showNotification('error', errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={notification.type === 'loading' ? undefined : hideNotification}
            />

            <FormHeader title="Modifier le mot de passe" back="/settings" onSave={handleSubmit} saving={isLoading}/>

            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-8 space-y-6 w-full">
                <FormSection
                    title="Vérification d'identité"
                    description="Pour votre sécurité, saisissez votre mot de passe actuel avant de le modifier."
                >
                    <div className="sm:w-1/2 sm:pr-2">
                        <PasswordField
                            id="current-password"
                            name="currentPassword"
                            label="Mot de passe actuel"
                            value={formData.currentPassword}
                            onChange={handleInputChange}
                            show={showPasswords.current}
                            onToggle={() => togglePasswordVisibility('current')}
                            placeholder="Votre mot de passe actuel"
                            autoComplete="current-password"
                        />
                    </div>
                </FormSection>

                <FormSection
                    title="Nouveau mot de passe"
                    description="8 caractères minimum ; mélangez majuscules, chiffres et symboles, sans informations personnelles ni ancien mot de passe."
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <PasswordField
                                id="new-password"
                                name="newPassword"
                                label="Nouveau mot de passe"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                show={showPasswords.new}
                                onToggle={() => togglePasswordVisibility('new')}
                                placeholder="Minimum 8 caractères"
                                autoComplete="new-password"
                            />
                            {/* Segmented strength bar */}
                            {strength && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex gap-1">
                                        {[0, 1, 2, 3].map(i => (
                                            <div key={i} className={`flex-1 h-1 rounded-full transition-all duration-300 ${i < strength.level ? strength.barColor : 'bg-gray-200 dark:bg-gray-700'}`}/>
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className={`text-xs font-semibold ${strength.textColor}`}>{strength.label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{strength.level}/4</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <PasswordField
                                id="confirm-password"
                                name="confirmPassword"
                                label="Confirmer le nouveau mot de passe"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                show={showPasswords.confirm}
                                onToggle={() => togglePasswordVisibility('confirm')}
                                placeholder="Répétez votre nouveau mot de passe"
                                autoComplete="new-password"
                            />
                            {formData.confirmPassword && (
                                <div className={`flex items-center gap-1.5 mt-1.5 text-xs font-medium ${passwordsMatch ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                                    {passwordsMatch
                                        ? <><CheckCircle size={12}/> Mots de passe identiques</>
                                        : <><XCircle size={12}/> Ne correspondent pas</>}
                                </div>
                            )}
                        </div>
                    </div>
                </FormSection>

                {/* Enter submits; the visible action is "Enregistrer" in the header */}
                <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}/>
            </form>
        </div>
    );
};

export default ChangePasswordLoggedIn;
